import { DataTable, Panel, Tag, type Column } from '@dayon/design-system';
import { manilaDay } from '../identity/record-words.ts';
import { KYC_REJECTION_REASONS, KYC_SUBMISSION_TONE, sentence } from './vocabulary.ts';
import type { KycRow } from './types.ts';

/**
 * THE OLDER KYC SUBMISSIONS, KEPT FOR THE RECORD.
 *
 * The owner closed the older process on 23 October 2026. Its intake, its approve and its
 * refusal went, and with them this tab's verdict controls, its documents panel and the
 * profile's "Review KYC". What a player sent through it stays readable for the retention
 * period: which submission, when it was sent, when and how it was decided. Nothing here can
 * be pressed, and no row is rewritten — a submission nobody decided before the close says so.
 *
 * Drawn only for a player who has one (KycTab). A player who never used the process is not
 * shown a table about it.
 */

/** The owner's sentence over the list, verbatim (23 Oct 2026). */
export const OLDER_PROCESS_CLOSED =
  'These were sent through the older process, which closed on 23 October 2026. They are kept for the record.';

/** What a submission nobody decided reads, in its Decided column. */
export const NOT_DECIDED = 'Not decided — the older process closed first.';

/** A refusal's reason in the words the refusal offered; a code with no words here still reads as words. */
export function refusalReasonWords(code: string): string {
  return KYC_REJECTION_REASONS.find((r) => r.code === code)?.label ?? sentence(code);
}

const COLUMNS: Column<KycRow>[] = [
  {
    key: 'ref',
    header: 'Reference',
    required: true,
    render: (k) => <span className="tabular">{k.ref}</span>,
  },
  {
    key: 'sent',
    header: 'Sent',
    render: (k) => <span className="text-muted tabular">{manilaDay(k.submitted_at)}</span>,
  },
  {
    key: 'decided',
    header: 'Decided',
    render: (k) => {
      if (k.reviewed_at === null) return <span className="text-subtle">{NOT_DECIDED}</span>;
      const by = k.reviewed_by_name ?? (k.reviewed_by ? 'an admin no longer on file' : null);
      return (
        <>
          <span className="text-muted tabular">{manilaDay(k.reviewed_at)}</span>
          {by && (
            <div className="text-subtle" style={{ fontSize: '11.5px' }}>
              by {by}
            </div>
          )}
        </>
      );
    },
  },
  {
    key: 'status',
    header: 'Status',
    render: (k) => (
      <>
        <Tag tone={KYC_SUBMISSION_TONE[k.status] ?? 'neutral'}>{sentence(k.status)}</Tag>
        {k.status === 'REJECTED' && k.rejection_reason ? (
          <div className="text-subtle" style={{ fontSize: '11.5px' }}>
            {refusalReasonWords(k.rejection_reason)}
          </div>
        ) : null}
      </>
    ),
  },
];

/** The list, under its sentence. Read-only: no column carries a control. */
export function OlderSubmissions({ submissions }: { submissions: KycRow[] }) {
  return (
    <Panel title="Older submissions">
      <p className="text-subtle" style={{ marginTop: 0 }}>
        {OLDER_PROCESS_CLOSED}
      </p>
      <DataTable
        caption="The KYC submissions this player sent through the older process, newest first: reference, when it was sent, when it was decided, and its status"
        columns={COLUMNS}
        rows={submissions}
        rowKey={(k) => k.id}
      />
    </Panel>
  );
}
